import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import PageHeader from "@/components/feature/PageHeader";
import { siteMeta, volumes, chapters } from "@/mocks/manga";

type Filter = "全部" | "单行本" | "连载" | "全彩" | "番外";

const filters: Filter[] = ["全部", "单行本", "连载", "全彩", "番外"];

const badgeClass: Record<string, string> = {
  单行本: "bg-primary-100 text-primary-700",
  连载: "bg-accent-100 text-accent-700",
  全彩: "bg-secondary-200 text-secondary-800",
  番外: "bg-primary-100 text-primary-700",
};

export default function Catalog() {
  const [filter, setFilter] = useState<Filter>("全部");

  const filtered = useMemo(
    () => (filter === "全部" ? chapters : chapters.filter((c) => c.category === filter)),
    [filter]
  );
  const showVolumes = filter === "全部" || filter === "单行本";

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "甜蜜惩罚漫画阅读目录",
    description: `${siteMeta.shortName}漫画阅读目录，按单行本卷、连载话、全彩版本、番外整理。`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "/" },
      { "@type": "ListItem", position: 2, name: "阅读目录", item: "/catalog" },
    ],
  };

  return (
    <>
      <Seo
        title={`甜蜜惩罚漫画阅读目录 - 单行本卷 · 连载话 · 全彩 · 番外 | ${siteMeta.name}`}
        description="甜蜜惩罚漫画阅读目录：按单行本卷、连载话、全彩版本、番外分类整理，找齐整部《甜蜜惩罚》漫画更方便。"
        keywords={siteMeta.keywords}
        canonicalPath="/catalog"
        schema={[collectionSchema, breadcrumbSchema]}
      />

      <PageHeader
        title="甜蜜惩罚漫画阅读目录"
        subtitle="按单行本卷、连载话、全彩版本、番外分类整理，找齐整部作品更方便。"
        crumbs={[{ label: "首页", to: "/" }, { label: "阅读目录" }]}
      >
        <div className="mt-7 inline-flex flex-wrap gap-1 rounded-full bg-background-100 p-1">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                filter === f
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "text-foreground-700 hover:bg-background-50"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {showVolumes && (
            <>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">单行本全卷</h2>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
                {volumes.map((v) => (
                  <Link key={v.id} to={`/read/${chapters.find((c) => c.volume === v.title)?.id || "c1"}`} className="group">
                    <div className="relative overflow-hidden rounded-xl bg-background-200/70">
                      <img
                        src={v.cover}
                        alt={`${v.title} ${v.subtitle} 封面`}
                        className="aspect-[3/4] w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                      <span className="absolute left-2 top-2 rounded-full bg-accent-500 px-2.5 py-0.5 text-[10px] font-semibold text-background-50">
                        {v.tag}
                      </span>
                    </div>
                    <div className="mt-2.5 px-0.5">
                      <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600">
                        {v.title} <span className="font-normal text-foreground-500">{v.subtitle}</span>
                      </h3>
                      <p className="mt-0.5 text-xs text-foreground-500">{v.count}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          <div className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {filter === "全部" ? "全部章节" : `${filter}章节`}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((c) => (
                <Link
                  key={c.id}
                  to={`/read/${c.id}`}
                  className="group flex items-center gap-4 rounded-2xl bg-background-100 p-4 transition-colors hover:bg-primary-100/70"
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-background-50 ${
                      c.isColor ? "bg-accent-500" : "bg-primary-500"
                    }`}
                  >
                    <i className={`${c.isColor ? "ri-palette-line" : "ri-book-open-line"} text-lg`}></i>
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${badgeClass[c.category]}`}>
                        {c.category}
                      </span>
                      <span className="text-xs text-foreground-500">{c.volume}</span>
                    </div>
                    <h3 className="mt-1 truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
                      {c.title}
                    </h3>
                  </div>
                  <i className="ml-auto ri-arrow-right-s-line text-xl text-foreground-400"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}